import { Box, Button, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import {
  AUTOMATED_CONNECTION_API_KEY_FINISH_ERROR,
  AUTOMATED_CONNECTION_API_KEY_PLACEHOLDER,
  AUTOMATED_CONNECTION_API_KEY_REJECTED_ERROR,
  AUTOMATED_CONNECTION_API_KEY_REPLACE_HELPER,
  AUTOMATED_CONNECTION_API_KEY_SAVE_FIRST_HELPER,
  AUTOMATED_CONNECTION_DEACTIVATE_DESCRIPTION,
  AUTOMATED_CONNECTION_DELETED_TEXT,
  AUTOMATED_CONNECTION_DELETE_ERROR_TEXT,
  AUTOMATED_CONNECTION_DELETE_MESSAGE,
  AUTOMATED_CONNECTION_DELETE_TITLE,
  AUTOMATED_CONNECTION_EDIT_TITLE,
  AUTOMATED_CONNECTION_FIELD_LABELS,
  AUTOMATED_CONNECTION_SAVED_INDICATOR_MS,
  AUTOMATED_CONNECTION_SAVE_ERROR_TEXT,
  AUTOMATED_CONNECTION_TEST_ERROR_TEXT,
  AUTOMATED_CONNECTION_TEST_SUCCESS_TEXT,
  CONNECTION_TYPES_SELECT_OPTIONS,
  NAME,
  STATUS,
  TOKEN,
  TYPE,
} from '../../../constants/automations';
import useActionAfterAutosave from '../../../hooks/useActionAfterAutosave';
import useAutosaveQueue from '../../../hooks/useAutosaveQueue';
import useDeleteWithConfirm from '../../../hooks/useDeleteWithConfirm';
import useRegisterCloseRequest from '../../../hooks/useRegisterCloseRequest';
import {
  deleteAutomatedConnectionRequest,
  testAutomatedConnectionRequest,
  updateAutomatedConnectionRequest,
} from '../../../store/actions/automations';
import { AutomationStatusEnum, IAutomatedConnection } from '../../../types/automations';
import { IApiError, NotificationListEnum } from '../../../types/shell';
import { omitConnectionToken } from '../../../utils/automations';
import notification from '../../../utils/notification';
import { handleApiError } from '../../../utils/ui';
import { AutomationStatusBadge } from '../../_mui/_components_/AutomationStatusBadge';
import { DeactivateSection } from '../../_mui/_components_/DeactivateSection';
import { FieldSaveIndicator } from '../../_mui/_components_/FieldSaveIndicator';
import { SidebarFormLayout } from '../../_mui/_components_/SidebarFormLayout';
import { SecretTextField } from '../../_mui/_controls_/SecretTextField';
import { DeleteConfirmationDialog } from '../../_mui/_dialogs_/DeleteConfirmationDialog';

interface IProps {
  connection: IAutomatedConnection;
  orgId: string;
  onClose?: () => void;
  closeRequestRef?: React.MutableRefObject<(() => void) | null>;
}

interface IEditConnectionFormValues {
  name: string;
  type: string;
  token: string;
}

const EDIT_VALIDATION_SCHEMA = yup.object().shape({
  [NAME]: yup.string().trim().required('Enter a name'),
  [TYPE]: yup.string().required('Select a type'),
});

const INDICATOR_SPACE = 48;
const INDICATOR_RIGHT = { input: 14, select: 40 };

const TYPE_SELECT_DISPLAY_PROPS = {
  'data-action': 'automated_connection_type_select',
  style: { paddingRight: INDICATOR_RIGHT.select + INDICATOR_SPACE },
} as React.HTMLAttributes<HTMLDivElement>;

const TEST_BUTTON_SX = { flex: '0 0 auto', height: 56, minWidth: 116, position: 'relative' } as const;

const TEST_SPINNER_SX = { position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' } as const;

const TOKEN_ACTIONS_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

const getTokenActionsSx = (isOpen: boolean) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  gap: 1,
  height: isOpen ? 38 : 0,
  opacity: isOpen ? 1 : 0,
  overflow: 'hidden',
  pointerEvents: isOpen ? 'auto' : 'none',
  transition: `height 200ms ${TOKEN_ACTIONS_EASING}, opacity 150ms ${TOKEN_ACTIONS_EASING}`,
});

export const buildConnectionTestErrorMessage = (error?: IApiError) => {
  const reason = error?.message?.trim();

  return reason ? `${AUTOMATED_CONNECTION_TEST_ERROR_TEXT} ${reason}` : AUTOMATED_CONNECTION_TEST_ERROR_TEXT;
};

const toConfirmedConnection = (next: IAutomatedConnection, saved?: unknown) =>
  omitConnectionToken({ ...next, ...(saved as Partial<IAutomatedConnection> | undefined) }) as IAutomatedConnection;

const noop = () => undefined;

const preventImplicitSubmit = (e: React.FormEvent<HTMLFormElement>) => e.preventDefault();

const EditAutomatedConnectionForm: React.FC<IProps> = ({ connection, orgId, onClose, closeRequestRef }) => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState(connection.status);
  const [isTesting, setIsTesting] = useState(false);
  const [isKeyFinishErrorShown, setIsKeyFinishErrorShown] = useState(false);
  const [keyRejectedReason, setKeyRejectedReason] = useState('');

  const form = useFormik<IEditConnectionFormValues>({
    initialValues: { [NAME]: connection.name ?? '', [TYPE]: connection.type ?? '', [TOKEN]: '' },
    validationSchema: EDIT_VALIDATION_SCHEMA,
    validateOnMount: true,
    onSubmit: noop,
  });

  const { values, errors, touched, getFieldProps, setFieldValue, setFieldTouched } = form;

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const tokenInputRef = useRef<HTMLInputElement>(null);

  const sendUpdate = useCallback(
    (_next: IAutomatedConnection, changed: Partial<IAutomatedConnection>) =>
      new Promise((resolve, reject) => {
        dispatch(
          updateAutomatedConnectionRequest({
            automatedConnection: { uid: connection.uid, ...changed },
            orgId,
            resolve,
            reject,
          }),
        );
      }),
    [dispatch, connection.uid, orgId],
  );

  const handleSaveError = useCallback(
    (error: unknown, rolledBack: (keyof IAutomatedConnection)[], savedConnection: IAutomatedConnection) => {
      rolledBack.forEach((field) => {
        if (field === STATUS) {
          setStatus(savedConnection.status);
        }

        if (field === NAME || field === TYPE) {
          setFieldValue(field, savedConnection[field] ?? '', true);
        }
      });

      handleApiError(AUTOMATED_CONNECTION_SAVE_ERROR_TEXT)(error as IApiError);
    },
    [setFieldValue],
  );

  const handleSaved = useCallback(
    (
      sent: Partial<IAutomatedConnection>,
      savedConnection: IAutomatedConnection,
      pendingFields: (keyof IAutomatedConnection)[],
    ) => {
      // The API answers 200 with status "failed" when Merge.dev rejects the key: keep the key so it can be fixed.
      const isKeyRejected = sent.token !== undefined && savedConnection.status === AutomationStatusEnum.Failed;

      if (isKeyRejected) {
        const reason = savedConnection.failure_reason?.trim() || AUTOMATED_CONNECTION_API_KEY_REJECTED_ERROR;

        setKeyRejectedReason(reason);
        handleApiError(AUTOMATED_CONNECTION_SAVE_ERROR_TEXT)({ message: reason } as IApiError);
      }

      if (!isKeyRejected && sent.token !== undefined && valuesRef.current[TOKEN].trim() === sent.token) {
        valuesRef.current = { ...valuesRef.current, [TOKEN]: '' };
        setFieldValue(TOKEN, '', false);
      }

      if (!pendingFields.includes(STATUS)) {
        setStatus(savedConnection.status);
      }
    },
    [setFieldValue],
  );

  const { confirmed, saveStates, isSaving, commit, discard, flush, getLatestValue } =
    useAutosaveQueue<IAutomatedConnection>({
      initial: toConfirmedConnection(connection),
      send: sendUpdate,
      toConfirmed: toConfirmedConnection,
      onError: handleSaveError,
      onSaved: handleSaved,
      savedMs: AUTOMATED_CONNECTION_SAVED_INDICATOR_MS,
    });

  const { isWaiting, runAfterAutosave } = useActionAfterAutosave(flush);

  const requestDelete = useCallback(
    (resolve: (value?: unknown) => void, reject: (reason?: unknown) => void) =>
      dispatch(deleteAutomatedConnectionRequest({ id: connection.uid, orgId, resolve, reject })),
    [dispatch, connection.uid, orgId],
  );

  const {
    isOpen: isDeletePanelOpen,
    isDeleting,
    open: openDeletePanel,
    cancel: handleCancelDelete,
    confirm: handleDelete,
  } = useDeleteWithConfirm({
    request: requestDelete,
    successText: AUTOMATED_CONNECTION_DELETED_TEXT,
    errorText: AUTOMATED_CONNECTION_DELETE_ERROR_TEXT,
    onClose,
  });

  const isBusy = useMemo(() => isSaving || isDeleting, [isSaving, isDeleting]);
  const isActionBlocked = useMemo(() => isDeleting || isWaiting, [isDeleting, isWaiting]);
  const newToken = useMemo(() => values[TOKEN].trim(), [values]);
  const isTokenChanged = useMemo(() => Boolean(newToken), [newToken]);
  const isTokenSaving = useMemo(() => saveStates.token === 'saving', [saveStates.token]);

  const getError = useCallback(
    (field: keyof IEditConnectionFormValues) => (touched[field] && errors[field] ? String(errors[field]) : undefined),
    [errors, touched],
  );

  const nameFieldProps = getFieldProps(NAME);
  const { onBlur: onNameFieldBlur } = nameFieldProps;

  const commitName = useCallback(
    (value: string) => {
      setFieldTouched(NAME, true, false);

      const trimmed = value.trim();

      if (!trimmed) {
        setFieldValue(NAME, getLatestValue(NAME), true);
        return;
      }

      if (trimmed !== value) {
        setFieldValue(NAME, trimmed, false);
      }

      commit(NAME, trimmed);
    },
    [commit, getLatestValue, setFieldTouched, setFieldValue],
  );

  const handleNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onNameFieldBlur(e);
      commitName(e.target.value);
    },
    [onNameFieldBlur, commitName],
  );

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitName((e.target as HTMLInputElement).value);
      }

      const savedName = getLatestValue(NAME);

      if (e.key === 'Escape' && saveStates.name !== 'saving' && (e.target as HTMLInputElement).value !== savedName) {
        e.preventDefault();
        setFieldValue(NAME, savedName, true);
      }
    },
    [commitName, saveStates.name, getLatestValue, setFieldValue],
  );

  const handleTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextType = e.target.value as IAutomatedConnection['type'];

      setFieldValue(TYPE, nextType);
      commit(TYPE, nextType);
    },
    [commit, setFieldValue],
  );

  const handleSaveToken = useCallback(() => {
    setIsKeyFinishErrorShown(false);
    setKeyRejectedReason('');

    if (newToken) {
      commit(TOKEN, newToken);
    }
  }, [commit, newToken]);

  const handleCancelToken = useCallback(() => {
    if (!isTokenSaving) {
      setIsKeyFinishErrorShown(false);
      setKeyRejectedReason('');
      discard(TOKEN);
      setFieldValue(TOKEN, '', false);
      tokenInputRef.current?.focus();
    }
  }, [isTokenSaving, discard, setFieldValue]);

  const handleTokenKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveToken();
      }
    },
    [handleSaveToken],
  );

  const handleDeactivateChange = useCallback(
    (checked: boolean) => {
      const nextStatus = checked ? AutomationStatusEnum.NotActive : AutomationStatusEnum.Active;

      setStatus(nextStatus);
      commit(STATUS, nextStatus);
    },
    [commit],
  );

  const handleTest = useCallback(() => {
    setIsTesting(true);

    return new Promise((resolve, reject) => {
      dispatch(testAutomatedConnectionRequest({ id: connection.uid, orgId, resolve, reject }));
    })
      .then(() => {
        notification.success(NotificationListEnum.Success, { content: AUTOMATED_CONNECTION_TEST_SUCCESS_TEXT });
      })
      .catch((error: IApiError) => {
        const message = buildConnectionTestErrorMessage(error);

        notification.error(message, { content: message });
      })
      .finally(() => {
        setIsTesting(false);
      });
  }, [dispatch, connection.uid, orgId]);

  const closeIfKeySaved = useCallback(() => {
    if (valuesRef.current[TOKEN].trim()) {
      setIsKeyFinishErrorShown(true);
      tokenInputRef.current?.focus();
      return;
    }

    onClose?.();
  }, [onClose]);

  const handleDone = useCallback(() => {
    if (!isDeleting) {
      runAfterAutosave(closeIfKeySaved);
    }
  }, [isDeleting, runAfterAutosave, closeIfKeySaved]);

  useRegisterCloseRequest(closeRequestRef, handleDone);

  const handleOpenDeletePanel = useCallback(() => {
    if (!isDeleting) {
      runAfterAutosave(openDeletePanel);
    }
  }, [isDeleting, runAfterAutosave, openDeletePanel]);

  const tokenHelper = useMemo(() => {
    if (isKeyFinishErrorShown) {
      return AUTOMATED_CONNECTION_API_KEY_FINISH_ERROR;
    }

    if (keyRejectedReason) {
      return keyRejectedReason;
    }

    return isTokenChanged
      ? AUTOMATED_CONNECTION_API_KEY_SAVE_FIRST_HELPER
      : AUTOMATED_CONNECTION_API_KEY_REPLACE_HELPER;
  }, [isKeyFinishErrorShown, keyRejectedReason, isTokenChanged]);

  const tokenFieldProps = getFieldProps(TOKEN);
  const { onChange: onTokenFieldChange } = tokenFieldProps;

  const handleTokenChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsKeyFinishErrorShown(false);
      setKeyRejectedReason('');
      onTokenFieldChange(e);
    },
    [onTokenFieldChange],
  );

  const nameError = useMemo(() => getError(NAME), [getError]);
  const typeError = useMemo(() => getError(TYPE), [getError]);

  return (
    <>
      <DeleteConfirmationDialog
        open={isDeletePanelOpen}
        title={AUTOMATED_CONNECTION_DELETE_TITLE}
        message={AUTOMATED_CONNECTION_DELETE_MESSAGE}
        onCancel={handleCancelDelete}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        dataAction="automated_connection_delete_dialog"
      />
      <SidebarFormLayout
        title={AUTOMATED_CONNECTION_EDIT_TITLE}
        onClose={handleDone}
        isCloseDisabled={isActionBlocked}
        headerContent={
          <AutomationStatusBadge status={confirmed.status} subject="Connection" saveState={saveStates.status} />
        }
        startActions={
          <Button
            variant="text"
            color="error"
            size="large"
            onClick={handleOpenDeletePanel}
            disabled={isActionBlocked}
            data-action="automated_connection_delete_btn"
          >
            Delete
          </Button>
        }
        actions={
          <Button
            variant="contained"
            size="large"
            onClick={handleDone}
            disabled={isActionBlocked}
            data-action="automated_connection_done_btn"
          >
            Done
          </Button>
        }
      >
        <form noValidate onSubmit={preventImplicitSubmit}>
          <Stack spacing={3} useFlexGap sx={{ pt: 1 }}>
            <TextField
              {...nameFieldProps}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              label={AUTOMATED_CONNECTION_FIELD_LABELS[NAME]}
              required
              fullWidth
              autoComplete="off"
              error={Boolean(nameError)}
              helperText={nameError}
              slotProps={{
                htmlInput: {
                  'data-action': 'automated_connection_name_input',
                  style: { paddingRight: INDICATOR_RIGHT.input + INDICATOR_SPACE },
                },
                input: {
                  endAdornment: <FieldSaveIndicator state={saveStates.name} right={INDICATOR_RIGHT.input} />,
                },
              }}
            />
            <TextField
              select
              name={TYPE}
              label={AUTOMATED_CONNECTION_FIELD_LABELS[TYPE]}
              value={values[TYPE]}
              onChange={handleTypeChange}
              required
              fullWidth
              error={Boolean(typeError)}
              helperText={typeError}
              slotProps={{
                select: { SelectDisplayProps: TYPE_SELECT_DISPLAY_PROPS },
                input: {
                  endAdornment: <FieldSaveIndicator state={saveStates.type} right={INDICATOR_RIGHT.select} />,
                },
              }}
            >
              {CONNECTION_TYPES_SELECT_OPTIONS.map(({ value, label, disabled }) => (
                <MenuItem key={value} value={value} disabled={disabled}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ flex: '1 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <SecretTextField
                  {...tokenFieldProps}
                  onChange={handleTokenChange}
                  inputRef={tokenInputRef}
                  error={isKeyFinishErrorShown || Boolean(keyRejectedReason)}
                  onKeyDown={handleTokenKeyDown}
                  label={AUTOMATED_CONNECTION_FIELD_LABELS[TOKEN]}
                  placeholder={AUTOMATED_CONNECTION_API_KEY_PLACEHOLDER}
                  helperText={tokenHelper}
                  hideToggleWhenEmpty
                  alwaysShrinkLabel
                  reserveIndicatorSpace
                  saveState={keyRejectedReason ? undefined : saveStates.token}
                  dataAction="automated_connection_token_input"
                />
                <Box aria-hidden={!isTokenChanged} data-testid="token-actions" sx={getTokenActionsSx(isTokenChanged)}>
                  <Button
                    variant="text"
                    onClick={handleCancelToken}
                    disabled={!isTokenChanged || isTokenSaving}
                    data-action="automated_connection_token_cancel_btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveToken}
                    disabled={!isTokenChanged || isBusy}
                    data-action="automated_connection_token_save_btn"
                  >
                    Save
                  </Button>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="large"
                onClick={handleTest}
                disabled={isBusy || isTesting || isTokenChanged}
                aria-busy={isTesting}
                aria-label="Test"
                sx={TEST_BUTTON_SX}
                data-action="automated_connection_test_btn"
              >
                <Box component="span" sx={{ visibility: isTesting ? 'hidden' : 'visible' }}>
                  Test
                </Box>
                {isTesting && <CircularProgress size={20} aria-label="Testing connection" sx={TEST_SPINNER_SX} />}
              </Button>
            </Box>
          </Stack>
          <Box sx={{ mt: 3 }}>
            <DeactivateSection
              title="Temporarily deactivate"
              description={AUTOMATED_CONNECTION_DEACTIVATE_DESCRIPTION}
              checked={status === AutomationStatusEnum.NotActive}
              onChange={handleDeactivateChange}
              dataAction="automated_connection_deactivate_toggle"
            />
          </Box>
        </form>
      </SidebarFormLayout>
    </>
  );
};

export default EditAutomatedConnectionForm;
